import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  FolderOpen,
  Info,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';

// 测评指标管理页面
// 页面结构：条件筛选卡片 + 指标所属单位列表 + 测评指标配置页面
export default function IndicatorManagementPage() {
  // ================= 页面状态 =================
  const [pageMode, setPageMode] = useState('list');
  const [currentUnit, setCurrentUnit] = useState('计算机学院');

  // ================= 指标所属单位数据 =================
  const unitList = [
    {
      id: 1,
      unit: '计算机学院',
      configStatus: '已配置',
    },
    {
      id: 2,
      unit: '文学院',
      configStatus: '未配置',
    },
    {
      id: 3,
      unit: '理学院',
      configStatus: '未配置',
    },
    {
      id: 4,
      unit: '马克思主义学院',
      configStatus: '已配置',
    },
  ];

  const openConfigPage = (unit) => {
    setCurrentUnit(unit);
    setPageMode('config');
  };

  if (pageMode === 'config') {
    return (
      <IndicatorConfigPage
        currentUnit={currentUnit}
        onBack={() => setPageMode('list')}
      />
    );
  }

  return (
    <div className="animate-fade-in space-y-4">
      {/* 条件筛选卡片：按指标所属单位和配置状态查询 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        <div className="flex items-end justify-between gap-6">
          <div className="flex gap-6">
            <FilterSelect
              label="所属单位"
              options={['全部', '计算机学院', '文学院', '理学院', '马克思主义学院']}
            />
            <FilterSelect
              label="配置状态"
              options={['全部', '已配置', '未配置']}
            />
          </div>

          <div className="flex gap-3">
            <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
              重置
            </button>
            <button className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]">
              查询
            </button>
          </div>
        </div>
      </div>

      {/* 结果卡片：指标所属单位列表 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        <div className="flex justify-between items-center mb-5">
          <SectionTitle title="指标所属单位列表" />
          <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
            批量同步配置
          </button>
        </div>

        {/* 指标所属单位表格 */}
        <div className="overflow-x-auto border border-gray-200 rounded-sm">
          <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap">
            <thead>
              <tr className="bg-[#fafafa] border-b border-gray-200">
                <TableHead className="w-[70px]">
                  序号
                </TableHead>
                <TableHead>
                  指标所属单位
                </TableHead>
                <TableHead className="text-center">
                  状态
                </TableHead>
                <TableHead className="text-center border-l border-gray-200">
                  操作
                </TableHead>
              </tr>
            </thead>
            <tbody>
              {unitList.map((row, index) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 hover:bg-[#f8f9fa]"
                >
                  <TableCell>
                    {index + 1}
                  </TableCell>
                  <TableCell>
                    <span className="font-semibold text-gray-900">
                      {row.unit}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <StatusTag status={row.configStatus} />
                  </TableCell>
                  <TableCell className="text-center border-l border-gray-100">
                    <button
                      onClick={() => openConfigPage(row.unit)}
                      className="text-[#1890ff] hover:underline"
                    >
                      测评指标配置
                    </button>
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 分页区域 */}
        <div className="flex justify-between items-center mt-6 text-[13px] text-gray-700">
          <div>
            共
            <span className="font-semibold mx-1 text-gray-900">
              {unitList.length}
            </span>
            条记录
          </div>
          <SimplePagination />
        </div>
      </div>
    </div>
  );
}

// 测评指标配置页面
function IndicatorConfigPage({ currentUnit, onBack }) {
  // ================= 页面状态 =================
  const [selectedNode, setSelectedNode] = useState({
    id: 'unit',
    name: currentUnit,
    levelText: currentUnit,
  });

  // ================= 左侧指标树 =================
  const treeData = [
    {
      id: 'unit',
      name: currentUnit,
      type: 'folder',
      children: [
        {
          id: 'de',
          name: '一、德育素质',
          type: 'folder',
          children: [
            {
              id: 'de-required',
              name: '必选项',
              type: 'folder',
              children: [
                {
                  id: 'de-required-basic',
                  name: '基本政治表现与日常行为...',
                  type: 'file',
                },
              ],
            },
            {
              id: 'de-bonus',
              name: '加分项',
              type: 'folder',
              children: [
                {
                  id: 'de-bonus-theory',
                  name: '理论学习类',
                  type: 'folder',
                  children: [
                    {
                      id: 'de-bonus-theory-1',
                      name: '参与“青年大学习”等线上...',
                      type: 'file',
                    },
                    {
                      id: 'de-bonus-theory-2',
                      name: '利用“学习强国”等平台...',
                      type: 'file',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ];

  // ================= 指标表格数据 =================
  const tableRows = getIndicatorRows(selectedNode, currentUnit);

  return (
    <div className="animate-fade-in -m-4 min-h-[calc(100vh-64px)] bg-white flex border border-gray-200">
      {/* 左侧指标树：按所属单位、德育素质、必选项/加分项分层 */}
      <div className="w-[300px] bg-[#fbfcfe] border-r border-gray-200 py-4 overflow-y-auto">
        <IndicatorTree
          nodes={treeData}
          selectedId={selectedNode.id}
          onSelect={setSelectedNode}
        />
      </div>

      {/* 右侧配置区：按钮 + 当前层级提示 + 指标表格 */}
      <div className="flex-1 bg-white p-6 overflow-x-auto">
        <div className="flex items-center justify-between mb-5">
          <div className="flex gap-3">
            <button className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9] flex items-center gap-1">
              <Plus size={15} />
              新增
            </button>
            <button className="px-5 py-2 border border-red-400 text-red-500 rounded text-sm hover:bg-red-50 flex items-center gap-1">
              <Trash2 size={15} />
              删除
            </button>
            <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50 flex items-center gap-1">
              <Upload size={15} />
              导入自动生成
            </button>
          </div>

          <button
            onClick={onBack}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
          >
            返回列表
          </button>
        </div>

        {/* 当前层级提示：说明导入自动生成的作用范围 */}
        <div className="border border-[#b7d7ff] bg-[#f0f7ff] text-[#1d6fdc] rounded px-4 py-3 text-[13px] flex items-center gap-2 mb-5">
          <Info size={16} />
          <span>
            当前层级：
            <span className="font-semibold mx-1">
              {selectedNode.levelText || selectedNode.name}
            </span>
            （根节点：支持导入Excel以一键自动生成并覆盖下级所有结构）
          </span>
        </div>

        {/* 指标配置表格：展示当前树节点下的指标配置 */}
        <div className="overflow-x-auto border border-gray-200 rounded-sm shadow-sm">
          <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap min-w-[1180px]">
            <thead>
              <tr className="bg-[#f4f6f8] border-b border-gray-200">
                <TableHead className="w-[60px] text-center">
                  序号
                </TableHead>
                <TableHead className="w-[260px]">
                  指标名称
                </TableHead>
                <TableHead className="w-[120px] text-center">
                  节点类型
                </TableHead>
                <TableHead className="w-[120px] text-center">
                  指标父类
                </TableHead>
                <TableHead className="w-[100px] text-center">
                  指标得分
                </TableHead>
                <TableHead className="w-[100px] text-center">
                  次数上限
                </TableHead>
                <TableHead className="w-[100px] text-center">
                  分数上限
                </TableHead>
                <TableHead className="w-[150px] text-center">
                  需要材料/配置
                </TableHead>
                <TableHead className="w-[90px] text-center">
                  备注
                </TableHead>
                <TableHead className="w-[80px] text-center">
                  排序
                </TableHead>
                <TableHead className="w-[130px] text-center">
                  操作
                </TableHead>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, index) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 hover:bg-[#f8f9fa]"
                >
                  <TableCell className="text-center">
                    {index + 1}
                  </TableCell>
                  <TableCell>
                    <span className="font-semibold text-gray-900">
                      {row.name}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="px-2 py-1 border border-gray-200 bg-[#fafafa] rounded text-gray-600">
                      {row.nodeType}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    {row.parent}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-[#1890ff] font-bold">
                      {row.score}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    {row.countLimit}
                  </TableCell>
                  <TableCell className="text-center">
                    {row.scoreLimit}
                  </TableCell>
                  <TableCell className="text-center text-gray-400">
                    {row.material}
                  </TableCell>
                  <TableCell className="text-center">
                    {row.remark}
                  </TableCell>
                  <TableCell className="text-center">
                    {row.sort}
                  </TableCell>
                  <TableCell className="text-center">
                    <button className="text-[#1890ff] hover:underline mr-3">
                      修改
                    </button>
                    <button className="text-[#1890ff] hover:underline">
                      删除
                    </button>
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// 左侧指标树
function IndicatorTree({ nodes, selectedId, onSelect }) {
  return (
    <div className="text-[13px] text-gray-700">
      {nodes.map((node) => (
        <TreeNode
          key={node.id}
          node={node}
          level={0}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

// 左侧树节点：文件夹节点可展开，文件节点用于定位具体指标
function TreeNode({ node, level, selectedId, onSelect }) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedId === node.id;
  const indent = 16 + level * 18;

  const handleClick = () => {
    onSelect({
      id: node.id,
      name: node.name,
      levelText: node.name,
    });

    if (hasChildren) {
      setExpanded(!expanded);
    }
  };

  return (
    <div>
      <div
        onClick={handleClick}
        className={[
          'h-[34px] flex items-center gap-1 cursor-pointer pr-3 hover:bg-[#eef6ff]',
          isSelected ? 'bg-[#e6f4ff] text-[#1890ff] font-semibold' : '',
        ].join(' ')}
        style={{ paddingLeft: indent }}
      >
        {hasChildren ? (
          expanded ? (
            <ChevronDown size={14} className="text-gray-400" />
          ) : (
            <ChevronRight size={14} className="text-gray-400" />
          )
        ) : (
          <span className="w-[14px]" />
        )}

        {node.type === 'file' ? (
          <FileText size={15} className="text-gray-400" />
        ) : expanded ? (
          <FolderOpen size={16} className="text-[#faad14]" />
        ) : (
          <Folder size={16} className="text-[#faad14]" />
        )}

        <span className="truncate">
          {node.name}
        </span>
      </div>

      {hasChildren && expanded && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// 根据当前树节点展示右侧表格数据
function getIndicatorRows(selectedNode, currentUnit) {
  const rows = {
    unit: [
      {
        id: 'de',
        name: '一、德育素质',
        nodeType: '非叶子节点',
        parent: currentUnit,
        score: '40',
        countLimit: '-',
        scoreLimit: '40',
        material: '-',
        remark: '一级指标',
        sort: '1',
      },
    ],
    de: [
      {
        id: 'required',
        name: '必选项',
        nodeType: '非叶子节点',
        parent: '一、德育素质',
        score: '28',
        countLimit: '-',
        scoreLimit: '28',
        material: '-',
        remark: '指标类型',
        sort: '1',
      },
      {
        id: 'bonus',
        name: '加分项',
        nodeType: '非叶子节点',
        parent: '一、德育素质',
        score: '12',
        countLimit: '-',
        scoreLimit: '12',
        material: '-',
        remark: '指标类型',
        sort: '2',
      },
    ],
    'de-required': [
      {
        id: 'basic',
        name: '基本政治表现与日常行为规范',
        nodeType: '叶子节点',
        parent: '必选项',
        score: '28',
        countLimit: '-',
        scoreLimit: '28',
        material: '配置',
        remark: '必选项',
        sort: '1',
      },
    ],
    'de-bonus': [
      {
        id: 'theory',
        name: '理论学习类',
        nodeType: '非叶子节点',
        parent: '加分项',
        score: '4',
        countLimit: '-',
        scoreLimit: '4',
        material: '-',
        remark: '二级分类',
        sort: '1',
      },
    ],
    'de-bonus-theory': [
      {
        id: 'youth-study',
        name: '参与“青年大学习”等线上主题团课',
        nodeType: '叶子节点',
        parent: '理论学习类',
        score: '1',
        countLimit: '2',
        scoreLimit: '2',
        material: '无',
        remark: '加分项',
        sort: '1',
      },
      {
        id: 'xuexiqiangguo',
        name: '利用“学习强国”等平台自觉开展理论学习',
        nodeType: '叶子节点',
        parent: '理论学习类',
        score: '1',
        countLimit: '-',
        scoreLimit: '2',
        material: '文字说明',
        remark: '加分项',
        sort: '2',
      },
    ],
  };

  return rows[selectedNode.id] || [
    {
      id: 'default',
      name: selectedNode.name,
      nodeType: selectedNode.id.includes('theory') ? '叶子节点' : '非叶子节点',
      parent: '理论学习类',
      score: '1',
      countLimit: '2',
      scoreLimit: '2',
      material: '无',
      remark: '加分项',
      sort: '1',
    },
  ];
}

// 条件筛选：下拉框
function FilterSelect({ label, options }) {
  return (
    <label className="block">
      <span className="block mb-2 text-sm text-gray-600">
        {label}
      </span>
      <select className="w-[170px] border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-[#1890ff] bg-white">
        {options.map((option) => (
          <option key={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

// 结果卡片标题
function SectionTitle({ title }) {
  return (
    <div className="flex items-center gap-2 text-[15px] font-bold text-gray-900">
      <span className="w-[3px] h-[14px] bg-[#1890ff]" />
      {title}
    </div>
  );
}

// 配置状态标签
function StatusTag({ status }) {
  const isConfigured = status === '已配置';

  return (
    <span
      className={[
        'inline-flex px-2 py-0.5 rounded border text-[12px]',
        isConfigured
          ? 'border-green-200 bg-green-50 text-green-600'
          : 'border-gray-200 bg-gray-50 text-gray-500',
      ].join(' ')}
    >
      {status}
    </span>
  );
}

// 表格表头
function TableHead({ children, className = '' }) {
  return (
    <th className={`py-3 px-4 font-medium text-gray-800 ${className}`}>
      {children}
    </th>
  );
}

// 表格单元格
function TableCell({ children, className = '' }) {
  return (
    <td className={`py-3 px-4 ${className}`}>
      {children}
    </td>
  );
}

// 简单分页：展示当前页和翻页按钮
function SimplePagination() {
  return (
    <div className="flex items-center gap-1">
      <button className="px-2 py-1 border border-gray-300 rounded text-gray-400 bg-gray-50">
        &lt;
      </button>
      <button className="px-3 py-1 bg-[#1890ff] text-white rounded">
        1
      </button>
      <button className="px-2 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-50">
        &gt;
      </button>
    </div>
  );
}
